import { PersonaService } from '@app/v2/personas/services/persona.service'
import { Persona } from '@app/v2/personas/types/persona.type'
import { AppService } from '@app/v2/shared/services/app.service'
import { Observable, forkJoin, map, of, switchMap, tap } from 'rxjs'
import { Event } from '../types/event.type'

const PERSONAS_PAGE_SIZE = 100

export type EventIdentifiersWorkspace = {
  name: string
  id: number
  slug: string
}

/**
 * Builds a pretty-printed JSON identifiers file body for a workspace, event, and personas.
 * @param event {Event} - The event.
 * @param personas {Persona[]} - All personas on the event.
 * @param workspace {EventIdentifiersWorkspace} - The current workspace.
 * @returns {string}
 */
export function buildEventIdentifiersContent(event: Event, personas: Persona[], workspace: EventIdentifiersWorkspace): string {
  return `${JSON.stringify(
    {
      workspace: {
        name: workspace.name ?? '',
        id: workspace.id,
        slug: workspace.slug ?? ''
      },
      event: {
        name: event.name ?? '',
        id: event.id,
        slug: event.slug ?? ''
      },
      personas: personas.map((persona) => ({
        name: persona.name ?? '',
        id: persona.id,
        slug: persona.slug ?? ''
      }))
    },
    null,
    2
  )}\n`
}

/**
 * Builds a filesystem-safe identifiers filename from workspace and event names.
 * @param workspaceName {string}
 * @param eventName {string}
 * @returns {string}
 */
export function buildEventIdentifiersFilename(workspaceName: string, eventName: string): string {
  const workspacePart = sanitizeFilenamePart(workspaceName) || 'workspace'
  const eventPart = sanitizeFilenamePart(eventName) || 'event'
  return `${workspacePart} ${eventPart} identifiers.txt`
}

/**
 * Loads every persona page for an event, then downloads the identifiers file.
 * @param params {{ event: Event; workspace: EventIdentifiersWorkspace; personaService: PersonaService; appService: AppService }}
 * @returns {Observable<void>}
 */
export function downloadEventIdentifiersFile(params: {
  event: Event
  workspace: EventIdentifiersWorkspace
  personaService: PersonaService
  appService: AppService
}): Observable<void> {
  const { event, workspace, personaService, appService } = params

  return fetchAllEventPersonas(personaService, event.workspace_id, event.id).pipe(
    tap((personas) => {
      const content = buildEventIdentifiersContent(event, personas, workspace)
      appService.downloadBlob(new Blob([content], { type: 'text/plain;charset=utf-8' }), buildEventIdentifiersFilename(workspace.name, event.name ?? ''))
    }),
    map(() => undefined)
  )
}

/**
 * Fetches all persona pages for an event.
 * @param personaService {PersonaService}
 * @param organizationId {number}
 * @param eventId {number}
 * @returns {Observable<Persona[]>}
 */
function fetchAllEventPersonas(personaService: PersonaService, organizationId: number, eventId: number): Observable<Persona[]> {
  return personaService.getPersonas(organizationId, eventId, PERSONAS_PAGE_SIZE, 1).pipe(
    switchMap((firstPage) => {
      const firstItems = firstPage.data ?? []
      const total = firstPage.total ?? firstItems.length
      const lastPage = Math.max(1, Math.ceil(total / PERSONAS_PAGE_SIZE))
      if (lastPage <= 1) return of(firstItems)

      const remainingPages$ = Array.from({ length: lastPage - 1 }, (_, index) => personaService.getPersonas(organizationId, eventId, PERSONAS_PAGE_SIZE, index + 2))

      return forkJoin(remainingPages$).pipe(map((pages) => firstItems.concat(...pages.map((page) => page.data ?? []))))
    })
  )
}

/**
 * Strips characters that are unsafe in filenames.
 * @param value {string}
 * @returns {string}
 */
function sanitizeFilenamePart(value: string): string {
  return value
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
